import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import ResponsiveImage from 'components/images/responsive-image'
import useIntersection from 'components/custom-hooks/useIntersection'

function isVideoPlaying(video) {
  if (!video) {
    return false
  }

  return (
    video.currentTime > 0 &&
    !video.paused &&
    !video.ended &&
    video.readyState > 2
  )
}

const Video = forwardRef(
  (
    {
      autoPlay,
      children,
      className,
      controls,
      deferMute,
      deferPause,
      deferPlay,
      deferThreshold,
      deferUnMute,
      enableObserver,
      firstFrameAsPoster,
      loop,
      muted,
      onEnded,
      onParentClick: customParentClick,
      onParentClickTogglePlayback,
      playsInline,
      poster,
      preload,
      ratio,
      scrollPlay,
      src,
      style,
    },
    forwardedRef,
  ) => {
    const ref = useRef()
    const videoRef = useRef()
    const [isAutoPaused, setIsAutoPaused] = useState(false)
    const [hidePoster, setHidePoster] = useState(false)
    const isIntersecting = enableObserver
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useIntersection(ref, { threshold: deferThreshold, once: false })
      : false

    function playVideo() {
      if (!hidePoster) {
        setHidePoster(true)
      }

      if (videoRef?.current?.play && !isVideoPlaying(videoRef?.current)) {
        videoRef.current.play()
      }
    }

    /* istanbul ignore next */
    function pauseVideo() {
      if (videoRef?.current?.pause && isVideoPlaying(videoRef?.current)) {
        videoRef.current.pause()
      }
    }

    function onParentClick(e) {
      if (e?.preventDefault) {
        e.preventDefault()
      }

      /* istanbul ignore next */
      if (!hidePoster) {
        setHidePoster(true)
        playVideo()
      } else if (videoRef?.current && onParentClickTogglePlayback) {
        if (isVideoPlaying(videoRef?.current)) {
          videoRef.current.pause()
        } else {
          playVideo()
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    function onDeferIn() {
      if (!isVideoPlaying(videoRef?.current) && scrollPlay && deferPlay) {
        if (!hidePoster) {
          setHidePoster(true)
        }

        videoRef.current.play()
      }

      if (deferUnMute) {
        videoRef.current.muted = false
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    function onDeferOut() {
      const isFullscreen =
        document.fullscreenElement ||
        videoRef.current.webkitDisplayingFullscreen

      if (isFullscreen) {
        return
      }

      /* istanbul ignore next */
      if (isVideoPlaying(videoRef?.current) && deferPause) {
        videoRef.current.pause()
      }

      if (deferMute) {
        videoRef.current.muted = true
      }
    }

    /* istanbul ignore next */
    function triggerFirstFrameAsPoster(e) {
      if (e?.target && !isAutoPaused) {
        e.target.pause()
        e.target.muted = false
        setIsAutoPaused(true)
      }
    }

    /* istanbul ignore next */
    useImperativeHandle(forwardedRef, () => ({
      resetVideo() {
        if (videoRef?.current) {
          videoRef.current.pause()
          videoRef.current.currentTime = 0
        }

        setHidePoster(false)
      },
      pauseVideo,
      playVideo,
      onParentClick,
    }))

    // Play the video if the fullscreen is active
    /* istanbul ignore next */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    function onFullScreenChange(e) {
      if (document.fullscreenElement || e?.target?.webkitDisplayingFullscreen) {
        requestAnimationFrame(playVideo)
      }
    }

    useEffect(() => {
      if (videoRef?.current) {
        // Support Safari / Chrome / Firefox
        videoRef.current.addEventListener(
          'webkitbeginfullscreen',
          onFullScreenChange,
        )
        videoRef.current.addEventListener(
          'webkitfullscreenchange',
          onFullScreenChange,
        )
        videoRef.current.addEventListener(
          'fullscreenchange',
          onFullScreenChange,
        )

        return () => {
          videoRef.current.removeEventListener(
            'webkitbeginfullscreen',
            onFullScreenChange,
          )
          videoRef.current.removeEventListener(
            'webkitfullscreenchange',
            onFullScreenChange,
          )
          // eslint-disable-next-line react-hooks/exhaustive-deps
          videoRef.current.removeEventListener(
            'fullscreenchange',
            onFullScreenChange,
          )
        }
      }

      return () => null
    }, [onFullScreenChange])

    useEffect(() => {
      if (videoRef?.current) {
        if (isIntersecting) {
          onDeferIn()
        } else {
          onDeferOut()
        }
      }
    }, [isIntersecting, onDeferIn, onDeferOut])

    const showPoster =
      poster && !isVideoPlaying(videoRef?.current) && !hidePoster

    return (
      <div
        className={clsx('video', className)}
        style={{ paddingTop: `${ratio * 100}%`, ...style }}
        ref={ref}
        onClick={customParentClick || onParentClick}
        onKeyUp={customParentClick || onParentClick}
        role="presentation"
      >
        {showPoster && (
          <ResponsiveImage alt="video poster" src={poster} imgRatio={ratio} />
        )}
        <video
          autoPlay={autoPlay}
          controls={controls}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          preload={preload}
          ref={videoRef}
          src={src}
          style={{ opacity: showPoster ? '0' : '1' }}
          type="video/mp4"
          {...(Boolean(onEnded) && { onEnded })}
          {...(firstFrameAsPoster && { onPlay: triggerFirstFrameAsPoster })}
        />
        {children}
      </div>
    )
  },
)

Video.defaultProps = {
  autoPlay: false,
  children: null,
  className: '',
  controls: true,
  deferMute: false,
  deferPause: true,
  deferPlay: true,
  deferThreshold: 0.75,
  deferUnMute: false,
  enableObserver: true,
  firstFrameAsPoster: false,
  loop: false,
  muted: true,
  onEnded: null,
  onParentClick: null,
  onParentClickTogglePlayback: true,
  playsInline: false,
  poster: '',
  posterBigDesktop: '100vw',
  posterDesktop: '100vw',
  posterLaptop: '100vw',
  posterPhone: '100vw',
  posterTablet: '100vw',
  preload: 'none',
  ratio: 1,
  scrollPlay: true,
  style: null,
  width: 100,
}

Video.propTypes = {
  autoPlay: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.number,
  ]),
  className: PropTypes.string,
  controls: PropTypes.bool,
  deferMute: PropTypes.bool,
  deferPause: PropTypes.bool,
  deferPlay: PropTypes.bool,
  deferThreshold: PropTypes.number,
  deferUnMute: PropTypes.bool,
  enableObserver: PropTypes.bool,
  firstFrameAsPoster: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  onEnded: PropTypes.func,
  onParentClick: PropTypes.func,
  onParentClickTogglePlayback: PropTypes.bool,
  playsInline: PropTypes.bool,
  poster: PropTypes.string,
  posterBigDesktop: PropTypes.string,
  posterDesktop: PropTypes.string,
  posterLaptop: PropTypes.string,
  posterPhone: PropTypes.string,
  posterTablet: PropTypes.string,
  preload: PropTypes.string,
  ratio: PropTypes.number,
  scrollPlay: PropTypes.bool,
  src: PropTypes.string.isRequired,
  style: PropTypes.shape({}),
  width: PropTypes.number,
}

export default Video
